import { Routes } from '@angular/router';
import { authenticationGuard } from './guards/authentication.guard';
import { NonProdGuard } from './guards/non-prod.guard';
import { subscriptionGuard } from './guards/subscription.guard';
import { courseGuard } from './guards/course.guard';
import { LoginComponent } from './components/login/login.component';
import { gradeBoosterGuard } from './guards/grade-booster.guard';
import { onboardingGuard } from './guards/onboarding.guard';
import { transferFrozenGuard } from './guards/transfer-frozen.guard';
import { askAaliyahAccessGuard } from './guards/ask-aaliyah-access.guard';
import { liveLessonAccessGuard } from './guards/live-lesson-access.guard';
import { essayMarkingAccessGuard } from './guards/essay-marking-access.guard';
import { onboardingWelcomeAccessGuard } from './guards/onboarding-welcome-access.guard';

export const routes: Routes = [
  {
    path: '',
    title: 'The Lightup Hub',
    component: LoginComponent,
  },
  {
    path: 'login',
    title: 'Login - The Lightup Hub',
    component: LoginComponent,
  },
  {
    path: 'login-as-user',
    title: 'Login as user',
    loadComponent: () =>
      import('./components/login/login-as-user/login-as-user.component').then(
        (m) => m.LoginAsUserComponent,
      ),
  },
  {
    path: 'subscribe/:id',
    title: 'Subscribe - The Lightup Hub',
    loadComponent: () =>
      import('./components/subscribe/subscribe.component').then(
        (m) => m.SubscribeComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard],
  },
  {
    path: 'subscribe-result',
    title: 'Verifying payment',
    loadComponent: () =>
      import('./components/subscribe-result/subscribe-result.component').then(
        (m) => m.SubscribeResultComponent,
      ),
  },
  {
    path: 'auth-check',
    loadComponent: () =>
      import('./components/auth-check/auth-check.component').then(
        (m) => m.AuthCheckComponent,
      ),
  },
  {
    path: 'account',
    title: 'Account - The Lightup Hub',
    loadComponent: () =>
      import('./components/account-settings/account-settings.component').then(
        (m) => m.AccountSettingsComponent,
      ),
    // Deliberately no transferFrozenGuard — a frozen account (subscription transfer pending)
    // still needs to reach settings: to check status, and to cancel the transfer if it hasn't
    // been accepted yet. See FrozenComponent's "Go to my account" link/redirects, which would
    // otherwise get bounced straight back to /onboarding/frozen by this guard.
    canActivate: [authenticationGuard],
  },
  {
    path: 'dashboard',
    title: 'Dashboard - The Lightup Hub',
    loadComponent: () =>
      import('./components/tlh_courses/courses/courses.component').then(
        (m) => m.CoursesComponent,
      ),
    canActivate: [authenticationGuard, onboardingGuard, transferFrozenGuard],
  },
  {
    path: 'grade-booster/:id',
    title: 'Grade Booster - The Lightup Hub',
    loadComponent: () =>
      import('./components/grade-booster/grade-booster.component').then(
        (m) => m.GradeBoosterComponent,
      ),
    canActivate: [subscriptionGuard, gradeBoosterGuard, transferFrozenGuard],
  },
  {
    // Disabled — redirects to /free-taster/viewer. To restore, swap this
    // back to `loadComponent: () => import('./components/free-taster/free-taster.component').then((m) => m.FreeTasterComponent)`.
    path: 'free-taster',
    redirectTo: '/free-taster/viewer',
    pathMatch: 'full',
  },
  {
    path: 'campaigns/:id',
    title: 'Campaign - The Lightup Hub',
    canActivate: [authenticationGuard],
    loadComponent: () =>
      import('./components/campaign-viewer/campaign-viewer.component').then(
        (m) => m.CampaignViewerComponent,
      ),
  },
  {
    path: 'packages',
    title: 'Packages - The Lightup Hub',
    canActivate: [authenticationGuard],
    loadComponent: () =>
      import('./components/packages-viewer/packages-viewer.component').then(
        (m) => m.PackagesViewerComponent,
      ),
  },
  {
    path: 'free-taster/viewer',
    title: 'Taster - The Lightup Hub',
    canActivate: [authenticationGuard, transferFrozenGuard],
    loadComponent: () =>
      import(
        './components/free-taster/free-taster-viewer/free-taster-viewer.component'
      ).then((m) => m.FreeTasterViewerComponent),
  },
  {
    path: 'course-lessons/:id',
    loadComponent: () =>
      import(
        './components/tlh_courses/course-lessons/course-lessons.component'
      ).then((m) => m.CourseLessonsComponent),
    canActivate: [subscriptionGuard, courseGuard, transferFrozenGuard],
  },
  {
    path: 'course-lessons/:id/:categoryId/:isEssaySkills',
    loadComponent: () =>
      import(
        './components/tlh_courses/course-lessons/course-lessons.component'
      ).then((m) => m.CourseLessonsComponent),
    canActivate: [subscriptionGuard, transferFrozenGuard],
  },
  {
    // Temporarily disabled — redirects to /packages. To restore, swap this
    // back to `loadComponent: () => import('./components/membership-leadpage/membership-leadpage.component').then((m) => m.MembershipLeadpageComponent)`.
    path: 'membership',
    redirectTo: '/packages',
    pathMatch: 'full',
  },
  {
    path: 'intro',
    title: 'Intro - The Lightup Hub',
    loadComponent: () =>
      import('./components/intro/intro.component').then(
        (m) => m.IntroComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard],
  },
  {
    path: 'onboarding',
    title: 'Onboarding - The Lightup Hub',
    canActivate: [authenticationGuard, transferFrozenGuard],
    loadComponent: () =>
      import('./components/onboarding/onboarding.component').then(
        (m) => m.OnboardingComponent,
      ),
  },
  {
    path: 'onboarding/welcome',
    title: 'Welcome - The Lightup Hub',
    canActivate: [authenticationGuard, transferFrozenGuard, onboardingWelcomeAccessGuard],
    loadComponent: () =>
      import('./components/onboarding/welcome/welcome.component').then(
        (m) => m.WelcomeComponent,
      ),
  },
  {
    path: 'onboarding/frozen',
    title: 'Account transferring - The Lightup Hub',
    canActivate: [authenticationGuard],
    loadComponent: () =>
      import('./components/onboarding/frozen/frozen.component').then(
        (m) => m.FrozenComponent,
      ),
  },
  {
    path: 'essay-marker-trial',
    title: 'Essay Marker - The Lightup Hub',
    canActivate: [authenticationGuard, transferFrozenGuard],
    loadComponent: () =>
      import(
        './components/essay-marker-trial/essay-marker-trial.component'
      ).then((m) => m.EssayMarkerTrialComponent),
  },
  {
    path: 'pdf-viewer',
    loadComponent: () =>
      import(
        './components/tlh_courses/lesson/pdf-viewer/pdf-viewer.component'
      ).then((m) => m.PdfViewerComponent),
  },
  {
    path: 'terms-and-conditions',
    title: 'Terms and Conditions - The Lightup Hub',
    loadComponent: () =>
      import(
        './components/terms-and-conditions/terms-and-conditions.component'
      ).then((m) => m.TermsAndConditionsComponent),
  },
  {
    path: 'faq',
    title: 'FAQ - The Lightup Hub',
    loadComponent: () =>
      import('./components/faq/faq.component').then(
        (m) => m.FaqComponent,
      ),
    canActivate: [authenticationGuard],
  },
  {
    path: 'forgot-password',
    title: 'Forgot Password - The Lightup Hub',
    loadComponent: () =>
      import('./components/forgot-password/forgot-password.component').then(
        (m) => m.ForgotPasswordComponent,
      ),
  },
  {
    path: 'set-password',
    title: 'Set Password - The Lightup Hub',
    loadComponent: () =>
      import('./components/set-password/set-password.component').then(
        (m) => m.SetPasswordComponent,
      ),
  },
  {
    path: 'reset-password',
    title: 'Reset Password - The Lightup Hub',
    loadComponent: () =>
      import('./components/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
  },
  {
    path: 'sandbox',
    title: 'Sandbox - The Lightup Hub',
    canActivate: [NonProdGuard],
    loadComponent: () =>
      import('./components/component-sandbox/component-sandbox.component').then(
        (m) => m.ComponentSandboxComponent,
      ),
  },
  {
    path: 'past-due-subscriptions',
    title: 'Past due subscriptions- The Lightup Hub',
    loadComponent: () =>
      import(
        './components/past-due-subscriptions/past-due-subscriptions.component'
      ).then((m) => m.PastDueSubscriptionsComponent),
  },
  {
    path: 'unpaid-subscriptions',
    title: 'Unpaid subscriptions- The Lightup Hub',
    loadComponent: () =>
      import(
        './components/unpaid-subscriptions/unpaid-subscriptions.component'
      ).then((m) => m.UnpaidSubscriptionsComponent),
  },
  {
    path: 'waiting-confirmation',
    title: 'Waiting Confirmation - The Lightup Hub',
    loadComponent: () =>
      import(
        './components/waiting-confirmation/waiting-confirmation.component'
      ).then((m) => m.WaitingConfirmationComponent),
  },
  // Essay Marker feature is deprecated/removed for now; routes are kept non-prod only for potential future re-introduction.
  {
    path: 'essay-marker',
    title: 'Essay Marker - The Lightup Hub',
    canActivate: [NonProdGuard],
    loadComponent: () =>
      import('./components/essay-marker/essay-marker.component').then(
        (m) => m.EssayMarkerComponent,
      ),
  },
  {
    path: 'essay-form',
    title: 'Essay Form - The Lightup Hub',
    canActivate: [NonProdGuard],
    loadComponent: () =>
      import(
        './components/essay-marker/ui/essay-form/essay-form.component'
      ).then((m) => m.EssayFormComponent),
  },
  {
    path: 'live-lesson',
    title: 'Live Lesson - The Lightup Hub',
    loadComponent: () =>
      import('./components/live-lesson/live-lesson.component').then(
        (m) => m.LiveLessonComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard, liveLessonAccessGuard],
  },
  {
    path: 'live-lesson/non-premium',
    title: 'Live Lesson - The Lightup Hub',
    loadComponent: () =>
      import('./components/live-lesson/live-lesson-iframe.component').then(
        (m) => m.LiveLessonIframeComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard],
  },
  {
    path: 'live-lesson/:id',
    title: 'Live Lesson - The Lightup Hub',
    loadComponent: () =>
      import('./components/live-lesson/live-lesson-detail/live-lesson-detail.component').then(
        (m) => m.LiveLessonDetailComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard, liveLessonAccessGuard],
  },
  {
    path: 'ask-aaliyah',
    title: 'Grade Jump Q+A - The Lightup Hub',
    loadComponent: () =>
      import('./components/ask-aaliyah/ask-aaliyah.component').then(
        (m) => m.AskAaliyahComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard, askAaliyahAccessGuard],
  },
  {
    path: 'ask-aaliyah/non-premium',
    title: 'Grade Jump Q+A - The Lightup Hub',
    loadComponent: () =>
      import('./components/ask-aaliyah/ask-aaliyah-iframe.component').then(
        (m) => m.AskAaliyahIframeComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard],
  },
  {
    path: 'ask-aaliyah/:id',
    title: 'Grade Jump Q+A - The Lightup Hub',
    loadComponent: () =>
      import('./components/ask-aaliyah/ask-aaliyah-detail.component').then(
        (m) => m.AskAaliyahDetailComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard, askAaliyahAccessGuard],
  },
  {
    path: 'essay-marking',
    title: 'Essay Marking - The Lightup Hub',
    loadComponent: () =>
      import('./components/essay-marking/essay-marking.component').then(
        (m) => m.EssayMarkingComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard, essayMarkingAccessGuard],
  },
  {
    path: 'essay-marking/history',
    title: 'Essay Marking - The Lightup Hub',
    loadComponent: () =>
      import('./components/essay-marking/essay-marking-history.component').then(
        (m) => m.EssayMarkingHistoryComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard, essayMarkingAccessGuard],
  },
  {
    path: 'essay-marking/course/:id',
    title: 'Essay Marking - The Lightup Hub',
    loadComponent: () =>
      import('./components/essay-marking/essay-marking-course.component').then(
        (m) => m.EssayMarkingCourseComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard, essayMarkingAccessGuard],
  },
  {
    path: 'essay-marking/course/:courseId/sub-course/:subCourseId',
    title: 'Essay Marking - The Lightup Hub',
    loadComponent: () =>
      import('./components/essay-marking/essay-marking-subcourse.component').then(
        (m) => m.EssayMarkingSubcourseComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard, essayMarkingAccessGuard],
  },
  {
    path: 'essay-marking/skill/:id',
    title: 'Essay Marking - The Lightup Hub',
    loadComponent: () =>
      import('./components/essay-marking/essay-marking-skill.component').then(
        (m) => m.EssayMarkingSkillComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard, essayMarkingAccessGuard],
  },
  {
    path: 'pack-purchase',
    loadComponent: () =>
      import('./components/pack-purchase/pack-purchase.component').then(
        (m) => m.PackPurchaseComponent,
      ),
    canActivate: [authenticationGuard, transferFrozenGuard],
  },
  {
    path: 'await-payment',
    loadComponent: () =>
      import('./components/await-payment/await-payment.component').then(
        (m) => m.AwaitPaymentComponent,
      ),
  },
  {
    path: 'pdf-viewer/:token',
    loadComponent: () =>
      import(
        './components/tlh_courses/lesson/chapter/common/pdf/streamed-pdf-window.component'
      ).then((m) => m.StreamedPdfWindowComponent),
  },
  {
    path: 'tlh-lesson/:origin/:courseId/:categoryId/:id/:structure',
    loadComponent: () =>
      import('./components/tlh_courses/lesson/lesson/lesson.component').then(
        (m) => m.LessonComponent,
      ),
    canActivate: [subscriptionGuard, transferFrozenGuard],
  },
  {
    path: 'tlh-lesson/:origin/:courseId/:categoryId/:id/:structure/:freeTaster',
    loadComponent: () =>
      import('./components/tlh_courses/lesson/lesson/lesson.component').then(
        (m) => m.LessonComponent,
      ),
  },
  {
    path: 'lmr-mocks',
    title: 'Mock Last-Minute Revision - The Lightup Hub',
    loadComponent: () =>
      import('./components/lmr-mocks/lmr-mocks.component').then(
        (m) => m.LmrMocksComponent,
      ),
    canActivate: [subscriptionGuard, transferFrozenGuard], // will add a guard later when backend is ready
  },
  {
    path: '**',
    pathMatch: 'full',
    title: '404 Not Found - The Lightup Hub',
    loadComponent: () =>
      import('./components/page-not-found/page-not-found.component').then(
        (m) => m.PageNotFoundComponent,
      ),
  },
];
